import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { datadogRum } from '@datadog/browser-rum';
import { Environment } from './app/config/environment';
import { isAnalyticsConsented, onAnalyticsConsentChange } from './app/utils/osano-analytics-consent';
import { scrubRumEventUrls } from './app/utils/datadog-url-scrubber';

if (window['stopAppLoad_IE']) {
	console.error('Sorry, the Internet Explorer browser is no longer supported!');
} else {
	console.info('environment.production?:', environment.production);

	if (environment.production) {
		enableProdMode();
	}
	platformBrowserDynamic()
		.bootstrapModule(AppModule)
		.then(moduleRef => {
			const injector = moduleRef.injector;
			const configService = injector.get(Environment); // Get the ConfigService instance

			// RUM ships inside main.js, so Osano cannot block it by URL the way it blocks gtm.js;
			// consent is mirrored through the SDK's own API instead.
			datadogRum.init({
				applicationId: '8a920592-5692-4e5c-89a6-3c7e6a27fdb2',
				clientToken: 'pub8e03b54ff3221f30ab365a71c449dbfa',
				// `site` refers to the Datadog site parameter of your organization
				// see https://docs.datadoghq.com/getting_started/site/
				site: 'datadoghq.com',
				service: 'myehe-ui',
				env: configService.ENV.env_key === 'PROD' ? 'production' : 'development',
				// Specify a version number to identify the deployed version of your application in Datadog
				// version: '1.0.0',
				sessionSampleRate: 100,
				sessionReplaySampleRate: 100,
				trackUserInteractions: true,
				trackResources: true,
				trackLongTasks: true,
				defaultPrivacyLevel: 'mask',
				// `mask` covers the replayed DOM but not the text Datadog lifts out of it. Action
				// names are derived from the element that was clicked, so without this a control
				// labelled with a patient's name is transmitted verbatim as the action name.
				enablePrivacyForActionName: true,
				// Nor does `mask` cover the URLs carried on every event.
				beforeSend: scrubRumEventUrls,
				// Read at init so a declined user never has RUM transmitting in the gap before the
				// listener below fires. The SDK does not persist consent, so re-derive every load.
				trackingConsent: isAnalyticsConsented() ? 'granted' : 'not-granted',
			});

			// index.html sets this flag only on the hosts configured in the Osano dashboard. Reaching
			// here without Osano.cm on one of them means osano.js failed to load: no banner and no
			// script blocking, so consent is silently unenforced. Everywhere else its absence is the
			// expected state, with the stub in play instead.
			if (window['osanoCmpExpected'] && !(window as any).Osano?.cm) {
				const cmpFailure = 'Osano CMP did not load on a host that requires it; consent is unenforced.';
				console.error(cmpFailure);
				datadogRum.addError(new Error(cmpFailure));
			}

			// Mirror later changes made from the Osano cookie drawer.
			onAnalyticsConsentChange(granted => {
				datadogRum.setTrackingConsent(granted ? 'granted' : 'not-granted');

				// Not gated on `granted` — this also carries Marketing and Personalization, which the
				// user can change without touching Analytics, and a DENY most needs propagating.
				(window as any).pushGoogleConsentUpdate?.();

				if (granted) {
					// Belt-and-braces: Osano re-runs the activation tag it blocked, but it does not
					// reload, so drive the re-enable ourselves too. loadGtm is idempotent.
					(window as any).loadGtm?.();
				}
			});

			if (configService.ENV.env_key === 'PROD') {
				// Silence non-error console output in production so nothing (incl. any future
				// credential/token/PHI logging) is exposed. console.error stays live for
				// genuine failures and Datadog capture.
				console.log = function () {};
				console.info = function () {};
				console.warn = function () {};
			}
		})
		.catch(err => {
			console.error('Bootstrap failed:', err);
			try {
				datadogRum.addAction('bootstrap_failure', {
					message: err instanceof Error ? err.message : String(err),
				});
			} catch {
				/* RUM may not be initialized yet */
			}
		});
}
